import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import {
  UserCircle, PencilSimple, Trash, MagnifyingGlass,
  LockOpen, ShieldWarning, Check, X, CaretDown
} from "@phosphor-icons/react";

const ROLE_OPTIONS = [
  { value: "admin", label: "Administrator" },
  { value: "team_leader", label: "Team Leader" },
  { value: "opportunity_finder", label: "Opportunity Finder (RP)" },
  { value: "senior_recruitment_partner", label: "Senior Recruitment Partner (SRP)" },
  { value: "candidate_finder", label: "Candidate Finder (CF)" },
];

const ROLE_COLORS = {
  admin: "bg-[#0F172A] text-[#C5A059]",
  team_leader: "bg-blue-100 text-blue-800",
  opportunity_finder: "bg-emerald-100 text-emerald-800",
  candidate_finder: "bg-violet-100 text-violet-800",
};

export default function UsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [lockedUsers, setLockedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleteMode, setDeleteMode] = useState("deactivate");
  const [showLocked, setShowLocked] = useState(false);
  const [tab, setTab] = useState("active");

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch { toast.error("Failed to load users"); }
    finally { setLoading(false); }
  }, []);

  const loadLocked = useCallback(async () => {
    try {
      const { data } = await api.get("/academy/admin/locked-users");
      setLockedUsers(data.items || []);
    } catch { /* ok if no locked users */ }
  }, []);

  const [onlineIds, setOnlineIds] = useState([]);
  const fetchOnline = useCallback(async () => {
    try { const { data } = await api.get("/users/online-status"); setOnlineIds(data.online_ids || []); } catch {}
  }, []);

  useEffect(() => { loadUsers(); loadLocked(); fetchOnline(); const i = setInterval(fetchOnline, 15000); return () => clearInterval(i); }, [loadUsers, loadLocked, fetchOnline]);

  const isOnline = (uid) => onlineIds.includes(uid);

  const openEdit = (u) => {
    setEditForm({ name: u.name, email: u.email, role: u.role, is_active: u.is_active, password: "", sender_email: u.sender_email || "" });
    setEditUser(u);
  };

  const saveEdit = async () => {
    try {
      const payload = { ...editForm };
      if (!payload.password) delete payload.password;
      await api.put(`/users/${editUser.id}`, payload);
      toast.success("User updated");
      setEditUser(null);
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/users/${deleteUser.id}?mode=${deleteMode}`);
      toast.success(deleteMode === "permanent" ? "User permanently deleted" : "User deactivated");
      setDeleteUser(null);
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const resetAttempts = async (locked) => {
    try {
      await api.post("/academy/admin/reset-attempts", { user_id: locked.user_id, module_id: locked.module_id });
      toast.success(`Attempts reset for ${locked.user_name}`);
      loadLocked();
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
  };

  const activeUsers = users.filter(u => u.is_active !== false);
  const inactiveUsers = users.filter(u => u.is_active === false);
  const displayedUsers = (tab === "active" ? activeUsers : inactiveUsers)
    .filter(u => !search || u.name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase()));

  const roleLabel = (r) => ROLE_OPTIONS.find(o => o.value === r)?.label || r;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="users-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#0F172A]">User Management</h1>
          <p className="text-sm text-[#6B7280] mt-0.5">{users.length} total users · {activeUsers.length} active</p>
        </div>
        {lockedUsers.length > 0 && (
          <Button
            variant="outline"
            className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
            onClick={() => setShowLocked(true)}
            data-testid="locked-users-btn"
          >
            <ShieldWarning size={16} weight="fill" />
            {lockedUsers.length} Locked
          </Button>
        )}
      </div>

      {/* Search + Tabs */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <Input
            placeholder="Search by name or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 h-9 text-sm"
            data-testid="users-search"
          />
        </div>
        <div className="flex gap-1 bg-[#F1F5F9] p-1 rounded-lg">
          <button
            onClick={() => setTab("active")}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${tab === "active" ? "bg-white text-[#0F172A] shadow-sm" : "text-[#6B7280]"}`}
            data-testid="tab-active"
          >
            Active ({activeUsers.length})
          </button>
          <button
            onClick={() => setTab("inactive")}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${tab === "inactive" ? "bg-white text-[#0F172A] shadow-sm" : "text-[#6B7280]"}`}
            data-testid="tab-inactive"
          >
            Inactive ({inactiveUsers.length})
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="border-b border-[#F1F5F9] bg-[#FAFAFA]">
                <th className="text-left px-4 py-3 font-semibold text-[#374151] text-xs uppercase tracking-wider">User</th>
                <th className="text-left px-4 py-3 font-semibold text-[#374151] text-xs uppercase tracking-wider">Role</th>
                <th className="text-left px-4 py-3 font-semibold text-[#374151] text-xs uppercase tracking-wider hidden sm:table-cell">Team</th>
                <th className="text-left px-4 py-3 font-semibold text-[#374151] text-xs uppercase tracking-wider hidden md:table-cell">Status</th>
                <th className="text-right px-4 py-3 font-semibold text-[#374151] text-xs uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayedUsers.map(u => (
                <tr key={u.id} className="border-b border-[#F3F4F6] hover:bg-[#FAFAFA] transition">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0F172A] to-[#334155] flex items-center justify-center text-white text-xs font-bold shadow-sm">
                          {u.name?.[0]?.toUpperCase() || "?"}
                        </div>
                        {isOnline(u.id) && <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 rounded-full border-2 border-white" data-testid={`user-online-${u.id}`} />}
                      </div>
                      <div>
                        <p className="font-semibold text-[#0F172A]">{u.name}</p>
                        <p className="text-xs text-[#6B7280]">{u.email}</p>
                        {u.sender_email && <p className="text-[10px] text-[#C5A059]">From: {u.sender_email}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${ROLE_COLORS[u.role] || "bg-gray-100 text-gray-800"}`}>
                      {roleLabel(u.role).split(" ")[0]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#6B7280] hidden sm:table-cell">{u.team_name || "—"}</td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium ${u.is_active !== false ? "text-emerald-700" : "text-red-600"}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${u.is_active !== false ? "bg-emerald-500" : "bg-red-500"}`} />
                      {u.is_active !== false ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openEdit(u)}
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-[#6B7280] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition"
                        title="Edit user"
                        data-testid={`edit-user-${u.id}`}
                      >
                        <PencilSimple size={15} />
                      </button>
                      {u.id !== user?.id && (
                        <button
                          onClick={() => { setDeleteUser(u); setDeleteMode("deactivate"); }}
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-[#6B7280] hover:bg-red-50 hover:text-red-600 transition"
                          title="Delete user"
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {displayedUsers.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-[#9CA3AF]">No users found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent className="max-w-md" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0F172A]">Edit User</DialogTitle>
            <DialogDescription>Update user details. Leave password blank to keep current.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs font-semibold text-[#374151] mb-1 block">Name</label>
              <Input value={editForm.name || ""} onChange={e => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-name" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#374151] mb-1 block">Email</label>
              <Input value={editForm.email || ""} onChange={e => setEditForm({ ...editForm, email: e.target.value })} data-testid="edit-email" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#374151] mb-1 block">Role</label>
              <select
                value={editForm.role || ""}
                onChange={e => setEditForm({ ...editForm, role: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-[#E5E7EB] text-sm bg-white"
                data-testid="edit-role"
              >
                {ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#374151] mb-1 block">New Password <span className="text-[#9CA3AF] font-normal">(optional)</span></label>
              <Input type="password" value={editForm.password || ""} onChange={e => setEditForm({ ...editForm, password: e.target.value })} placeholder="Leave blank to keep current" data-testid="edit-password" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#374151] mb-1 block">Sender Email <span className="text-[#9CA3AF] font-normal">(From address for outgoing emails)</span></label>
              <select
                value={editForm.sender_email || ""}
                onChange={e => setEditForm({ ...editForm, sender_email: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-[#E5E7EB] text-sm bg-white"
                data-testid="edit-sender-email"
              >
                <option value="">Not set (uses default)</option>
                <option value="admin@banksmorgan.com">admin@banksmorgan.com</option>
                {editUser?.name && <option value={`${editUser.name.toLowerCase().replace(/\s+/g, ".")}@banksmorgan.com`}>{editUser.name.toLowerCase().replace(/\s+/g, ".")}@banksmorgan.com</option>}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setEditForm({ ...editForm, is_active: !editForm.is_active })}
                className={`w-10 h-6 rounded-full transition p-0.5 ${editForm.is_active ? "bg-emerald-500" : "bg-[#D1D5DB]"}`}
                data-testid="edit-active-toggle"
              >
                <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${editForm.is_active ? "translate-x-4" : "translate-x-0"}`} />
              </button>
              <span className="text-xs text-[#6B7280]">Account {editForm.is_active ? "Active" : "Inactive"}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditUser(null)}>Cancel</Button>
            <Button onClick={saveEdit} className="bg-[#0F172A] text-white hover:bg-[#1E293B]" data-testid="save-edit-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteUser} onOpenChange={() => setDeleteUser(null)}>
        <DialogContent className="max-w-md" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0F172A]">Remove User</DialogTitle>
            <DialogDescription>Choose how to remove <strong>{deleteUser?.name}</strong></DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <button
              onClick={() => setDeleteMode("deactivate")}
              className={`w-full p-3 rounded-lg border text-left transition ${deleteMode === "deactivate" ? "border-[#C5A059] bg-[#FFFBEB]" : "border-[#E5E7EB] hover:bg-[#FAFAFA]"}`}
              data-testid="deactivate-option"
            >
              <p className="text-sm font-semibold text-[#0F172A]">Deactivate</p>
              <p className="text-xs text-[#6B7280] mt-0.5">Disable login but keep all records. Can be reactivated later.</p>
            </button>
            <button
              onClick={() => setDeleteMode("permanent")}
              className={`w-full p-3 rounded-lg border text-left transition ${deleteMode === "permanent" ? "border-red-400 bg-red-50" : "border-[#E5E7EB] hover:bg-[#FAFAFA]"}`}
              data-testid="permanent-option"
            >
              <p className="text-sm font-semibold text-red-700">Permanently Delete</p>
              <p className="text-xs text-[#6B7280] mt-0.5">Remove user and all associated data. This cannot be undone.</p>
            </button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteUser(null)}>Cancel</Button>
            <Button
              onClick={confirmDelete}
              className={deleteMode === "permanent" ? "bg-red-600 text-white hover:bg-red-700" : "bg-[#0F172A] text-white hover:bg-[#1E293B]"}
              data-testid="confirm-delete-btn"
            >
              {deleteMode === "permanent" ? "Permanently Delete" : "Deactivate User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Locked Users Dialog */}
      <Dialog open={showLocked} onOpenChange={setShowLocked}>
        <DialogContent className="max-w-lg" data-testid="locked-users-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#0F172A]">
              <ShieldWarning size={20} className="text-amber-500" weight="fill" />
              Academy Locked Users
            </DialogTitle>
            <DialogDescription>Users who have exceeded maximum assessment attempts</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2 max-h-[400px] overflow-y-auto">
            {lockedUsers.length === 0 ? (
              <p className="text-sm text-[#9CA3AF] text-center py-6">No locked users</p>
            ) : lockedUsers.map((l, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-[#FAFAFA] rounded-lg border border-[#F1F5F9]">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 text-xs font-bold">
                    {l.user_name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#0F172A]">{l.user_name}</p>
                    <p className="text-xs text-[#6B7280]">{l.module_title} · {l.attempts}/{l.max_attempts} attempts · Score: {l.last_score}%</p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  onClick={() => resetAttempts(l)}
                  data-testid={`reset-${l.user_id}-${l.module_id}`}
                >
                  <LockOpen size={14} /> Reset
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
